import {Fade, Stack, Typography} from '@mui/material';
import React from 'react';

import {AlertInfo, TagChip} from 'presentation/components';
import {DateFormatEnum, FeaturesEnum} from 'presentation/constants';
import {useContractStatus} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {planTypeOptions} from 'presentation/pages';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {formatText, stringfyDate} from 'presentation/utils';
import {getPartnerLogo} from '../../utils/getPartnerLogo';
import {CopyButton} from '../CopyButton';
import CustomFooter from './CustomFooter';
import {
	StyledBox,
	StyledCard,
	StyledCardHeader,
	StyledChip,
	StyledHeaderLeftColumn,
	StyledHeaderTopRow,
	StyledPartnerLogoWrapper,
	StyledText,
	StyledSubtitle,
	StyledTitle,
} from './styles';
import {HealthCardProps} from './types';

const {cancelContractIn, cancelContract, suspendedContractAlert, network} =
	translations['pt-br'].commons;
const {cardCode} = translations['pt-br'].privateFlow.healthCard.cardData;

export function HealthCard({data, hiddenCopyIcon}: HealthCardProps) {
	const {
		nmPlano,
		nmSocialUsuarioC,
		nmUsuarioC,
		tipoUsuarioC,
		cdUsuarioC,
		cdParceiroLogo,
		flPlanoIntegrado,
		cdModeloCarteira,
		redeCarteirinha,
	} = data;

	const {isValid, contractCancellationDate, isContractSuspended} = useContractStatus();
	const {isEnabled: isPartnerLogoEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_CARTEIRINHA_LAYOUT,
	);

	const formattedUserName = formatText(nmSocialUsuarioC || nmUsuarioC);
	const formattedChipLabel = formatText(tipoUsuarioC);
	const cancelContractLabel = contractCancellationDate ? cancelContractIn : cancelContract;
	const partnerLogo =
		cdParceiroLogo && isPartnerLogoEnabled ? getPartnerLogo({cdParceiroLogo}) : undefined;

	const alertLabel = !isValid ? cancelContractLabel : suspendedContractAlert;
	const alertDate = !isValid ? contractCancellationDate : null;

	const {
		beneficiary: {tipoPlanoC},
	} = useAccountDataStore(store => store.accountData);

	return (
		<Fade in timeout={1500}>
			<StyledCard elevation={0}>
				{!isValid || isContractSuspended ? (
					<AlertInfo.Root
						severity="warning"
						sx={{
							padding: '0 0 0 8px',
							borderRadius: '6px 6px 0 0',
						}}>
						<AlertInfo.Description>
							<Typography fontSize={'0.875rem'} component={'span'}>
								{alertLabel}
								<Typography
									component={'span'}
									fontSize={'inherit'}
									fontWeight={700}>
									{alertDate
										? stringfyDate(alertDate, DateFormatEnum.DATE_FORMAT_8)
										: ''}
								</Typography>
							</Typography>
						</AlertInfo.Description>
					</AlertInfo.Root>
				) : null}
				<StyledBox>
					<StyledCardHeader>
						<StyledHeaderLeftColumn>
							<StyledHeaderTopRow>
								<TagChip
									label={planTypeOptions[tipoPlanoC].label}
									color={planTypeOptions[tipoPlanoC].color}
								/>
							</StyledHeaderTopRow>
							<StyledSubtitle sx={{fontSize: '0.875rem'}}>{nmPlano}</StyledSubtitle>
						</StyledHeaderLeftColumn>
						{partnerLogo ? (
							<StyledPartnerLogoWrapper>
								<img src={partnerLogo.logo} alt={partnerLogo.alt} />
							</StyledPartnerLogoWrapper>
						) : null}
					</StyledCardHeader>
					<StyledTitle>{formattedUserName}</StyledTitle>
					<Stack direction="row" alignItems="baseline" gap={2}>
						<Stack>
							<StyledSubtitle>{cardCode}</StyledSubtitle>
							<Stack direction="row" gap={0.5} alignItems="center">
								<StyledText>{cdUsuarioC}</StyledText>
								{!hiddenCopyIcon && <CopyButton value={cdUsuarioC} iconSize={12} />}
							</Stack>
						</Stack>

						<Stack>
							<StyledSubtitle>{network}</StyledSubtitle>

							<StyledText>{redeCarteirinha}</StyledText>
						</Stack>
						<StyledChip label={formattedChipLabel} />
					</Stack>
				</StyledBox>
				<CustomFooter
					flPlanoIntegrado={flPlanoIntegrado}
					planType={cdModeloCarteira}
					cdModeloCarteira={cdModeloCarteira}
					cdParceiroLogo={(partnerLogo && cdParceiroLogo) || ''}
				/>
			</StyledCard>
		</Fade>
	);
}
